/**
 * Commissions Hub (UI-2 v3 layout, BE-6a/b data)
 *
 * Tabs: Overview (grouped by recipient, Pay per recipient) | Pending | Payable | Paid | Payments.
 * Commissions are earned at invoice finalisation by the internal-task assignee and become payable once the customer
 * has fully paid the invoice; payouts pay whole commissions. Payments can be reversed; money owed back after a void
 * is recovered or written off.
 * Route: /accounting/commissions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, Alert, Card, CardContent, Collapse, Button, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, TablePagination, Snackbar, Link,
} from '@mui/material';
import { ExpandMore, ExpandLess, Payment, Settings, People, Undo as ReverseIcon } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { SummaryCardGrid, DataTable, StatusChip, CurrencyText, JournalEntryLink } from '../../components/accounting';
import CommissionPaymentDialog from '../../components/accounting/CommissionPaymentDialog';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { can } from '../../utils/roleUtils';
import { commissionService, RECIPIENT_TYPES } from '../../services/accounting/commission.service';
import { serverMessage } from '../../utils/apiConvert';

const TAB_STATUS = { 1: 'PENDING', 2: 'PAYABLE', 3: 'PAID' };
const PAYMENTS_TAB = 4;
export const TYPE_TONES = { EXTERNAL_AGENT: 'primary', EXTERNAL_PRO: 'info', INTERNAL_STAFF: 'success' };
const DIRECTION = {
  PAID: { label: 'Paid to agent', tone: 'success' },
  RECOVERED: { label: 'Recovered', tone: 'info' },
  WRITTEN_OFF: { label: 'Written off', tone: 'warning' },
};

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

export const commissionStatus = (c) => {
  if (Number(c.amount) < 0 || c.reversalOf) return { tone: 'default', label: 'Reversed (invoice voided)' };
  if (Number(c.paid) > 0) return { tone: 'success', label: 'Paid' };
  if (Number(c.payable) > 0) return { tone: 'warning', label: 'Payable' };
  return { tone: 'info', label: 'Waiting for customer payment' };
};

export const rateText = (c) => (c.rateType === 'PERCENTAGE' ? `${Number(c.rateValue)}%` : formatCurrency(c.rateValue));

const stateOf = (c) => {
  if (Number(c.amount) < 0 || c.reversalOf) return 'REVERSED';
  if (Number(c.paid) > 0) return 'PAID';
  if (Number(c.payable) > 0) return 'PAYABLE';
  return 'PENDING';
};

export const TypeChip = ({ type }) => <StatusChip tone={TYPE_TONES[type] || 'default'} label={RECIPIENT_TYPES[type] || type} />;

const Commissions = () => {
  const navigate = useNavigate();
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [tab, setTab] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [counts, setCounts] = useState({ PENDING: 0, PAYABLE: 0 });
  const [rows, setRows] = useState({ content: [], totalElements: 0 });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [groupRows, setGroupRows] = useState({});
  const [payDialog, setPayDialog] = useState(null);
  const [reverse, setReverse] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const loadHeader = useCallback(async () => {
    try {
      const [recs, pending, payable] = await Promise.all([
        commissionService.getRecipients(),
        commissionService.getCommissions({ status: 'PENDING', page: 0, size: 1 }),
        commissionService.getCommissions({ status: 'PAYABLE', page: 0, size: 1 }),
      ]);
      setRecipients(recs);
      setCounts({ PENDING: pending.totalElements, PAYABLE: payable.totalElements });
    } catch (err) {
      setError(serverMessage(err, 'Failed to load commissions'));
    }
  }, []);

  const loadRows = useCallback(async () => {
    if (tab === 0) { setLoading(false); return; }
    setLoading(true);
    try {
      setRows(tab === PAYMENTS_TAB
        ? await commissionService.getPayments({ page, size })
        : await commissionService.getCommissions({ status: TAB_STATUS[tab], page, size }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [tab, page, size]);

  useEffect(() => { loadHeader(); }, [loadHeader]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const loadGroup = useCallback(async (id) => {
    setGroupRows((g) => ({ ...g, [id]: { loading: true, content: [], totalElements: 0 } }));
    try {
      const pageData = await commissionService.getCommissions({ recipientId: id, page: 0, size: 100 });
      setGroupRows((g) => ({ ...g, [id]: { loading: false, ...pageData } }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load commissions'));
    }
  }, []);

  const refresh = () => {
    loadHeader();
    loadRows();
    Object.keys(expanded).filter((id) => expanded[id]).forEach(loadGroup);
  };

  const toggleExpand = (id) => {
    const open = !expanded[id];
    setExpanded((prev) => ({ ...prev, [id]: open }));
    if (open) loadGroup(id);
  };

  const sum = (k) => recipients.reduce((t, r) => t + Number(r[k] || 0), 0);
  const owedBack = sum('receivable');
  const summaryCards = [
    { label: 'Earned (all time)', value: sum('earned'), color: 'primary' },
    { label: 'Payable Now', value: sum('payable'), color: 'warning' },
    { label: 'Paid (all time)', value: sum('paid'), color: 'success' },
    { label: 'Outstanding', value: sum('outstanding'), color: 'error' },
  ];
  const groups = recipients.filter((r) => ['earned', 'paid', 'outstanding', 'receivable'].some((k) => Number(r[k] || 0) !== 0));

  const COLUMNS = [
    { key: 'task', label: 'Task', width: '14%', render: (r) => r.taskNumber || r.taskName || '—' },
    { key: 'invoiceNumber', label: 'Invoice', width: '10%', render: (r) => (
      <Link component="button" underline="hover" sx={{ fontSize: fs.xs }} onClick={() => navigate(`/invoices/${r.invoiceId}`)}>{r.invoiceNumber}</Link>
    ) },
    { key: 'recipient', label: 'Recipient', width: '13%', render: (r) => r.recipient?.name || '—' },
    { key: 'rate', label: 'Rate', width: '7%', render: rateText },
    { key: 'basisAmount', label: 'SC Amount', width: '9%', align: 'right', render: (r) => <CurrencyText amount={r.basisAmount} /> },
    { key: 'amount', label: 'Commission', width: '9%', align: 'right', render: (r) => <CurrencyText amount={r.amount} /> },
    { key: 'paid', label: 'Paid', width: '8%', align: 'right', render: (r) => <CurrencyText amount={r.paid} /> },
    { key: 'remaining', label: 'Remaining', width: '9%', align: 'right', render: (r) => (
      <CurrencyText amount={stateOf(r) === 'REVERSED' ? 0 : Number(r.amount) - Number(r.paid || 0)} />
    ) },
    { key: 'status', label: 'Status', width: '11%', render: (r) => (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        <StatusChip type="commissionStatus" value={stateOf(r)} />
        {r.owedBack && <StatusChip tone="error" label="Owes back" />}
      </Box>
    ) },
    { key: 'createdAt', label: 'Date', width: '9%', render: (r) => (
      <Box display="flex" alignItems="center" gap={0.5}>
        {formatDate(r.createdAt)}
        <JournalEntryLink id={r.journalEntryId} title="View accrual entry" />
      </Box>
    ) },
  ];

  const PAYMENT_COLUMNS = [
    { key: 'paymentNumber', label: 'Payment #', width: '12%', render: (p) => <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: 'primary.main' }}>{p.paymentNumber}</Typography> },
    { key: 'paidAt', label: 'Date', width: '9%', render: (p) => formatDate(p.paidAt) },
    { key: 'recipient', label: 'Recipient', width: '12%', render: (p) => p.recipient?.name || '—' },
    { key: 'direction', label: 'Type', width: '11%', render: (p) => <StatusChip tone={DIRECTION[p.direction]?.tone} label={DIRECTION[p.direction]?.label || p.direction} /> },
    { key: 'amount', label: 'Amount', width: '10%', align: 'right', render: (p) => <CurrencyText amount={p.amount} /> },
    { key: 'accountName', label: 'Account', width: '11%', render: (p) => p.accountName || '—' },
    { key: 'paidTo', label: 'Paid to', width: '11%', render: (p) => (p.paidToBank || p.paidToIban
      ? [p.paidToBank, p.paidToIban && `•••• ${String(p.paidToIban).slice(-4)}`].filter(Boolean).join(' ') : '—') },
    { key: 'reference', label: 'Notes', width: '11%', render: (p) => p.reference || '—' },
    { key: 'status', label: 'Status', width: '9%', render: (p) => (
      <Box>
        <StatusChip tone={p.status === 'REVERSED' ? 'default' : 'success'} label={p.status === 'REVERSED' ? 'Reversed' : 'Recorded'} />
        {p.reversalReason && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.25 }}>{p.reversalReason}</Typography>}
      </Box>
    ) },
    { key: 'actions', label: '', width: '7%', align: 'right', render: (p) => (
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
        <JournalEntryLink id={p.journalEntryId} />
        {canWrite && p.status === 'RECORDED' && (
          <Tooltip title="Reverse payment">
            <IconButton size="small" color="error" onClick={() => { setReason(''); setReverse(p); }}><ReverseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Tooltip>
        )}
      </Box>
    ) },
  ];

  const confirmReverse = async () => {
    setBusy(true);
    try {
      await commissionService.reversePayment(reverse.id, reason.trim());
      setSnackbar('Payment reversed');
      setReverse(null);
      refresh();
    } catch (err) {
      setError(serverMessage(err, 'The reversal failed'));
      setReverse(null);
    } finally {
      setBusy(false);
    }
  };

  const tabSx = { textTransform: 'none', fontSize: fs.sm };

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600} color="secondary.main">Commissions</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" startIcon={<Settings />} onClick={() => navigate('/accounting/commissions/config')}
            sx={{ textTransform: 'none', fontSize: fs.sm }}>Config</Button>
          <Button variant="contained" size="small" startIcon={<People />} onClick={() => navigate('/accounting/commissions/recipients')}
            sx={{ textTransform: 'none', fontSize: fs.sm }}>Recipients</Button>
        </Box>
      </Box>

      {owedBack > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={canWrite && (
            <Box display="flex" gap={1}>
              <Button color="inherit" size="small" onClick={() => setPayDialog({ direction: 'RECOVERED' })}>Recover</Button>
              <Button color="inherit" size="small" onClick={() => setPayDialog({ direction: 'WRITTEN_OFF' })}>Write off</Button>
            </Box>
          )}>
          Recipients owe back {formatCurrency(owedBack)} — commission paid out on invoices that were later voided.
        </Alert>
      )}

      <SummaryCardGrid cards={summaryCards} sx={{ mb: 2 }} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: 0, minWidth: '100%' }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); setRows({ content: [], totalElements: 0 }); }} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" sx={tabSx} />
          <Tab label={`Pending (${counts.PENDING})`} sx={tabSx} />
          <Tab label={`Payable (${counts.PAYABLE})`} sx={tabSx} />
          <Tab label="Paid" sx={tabSx} />
          <Tab label="Payments" sx={tabSx} />
        </Tabs>
      </Box>

      {tab === 0 && (
        groups.length === 0 ? (
          <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', py: 4, textAlign: 'center' }}>No commissions recorded.</Typography>
        ) : groups.map((group) => {
          const g = groupRows[group.id];
          return (
            <Card variant="outlined" sx={{ mb: 1.5 }} key={group.id}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 1, flexWrap: 'wrap' }}
                  onClick={() => toggleExpand(group.id)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small">{expanded[group.id] ? <ExpandLess /> : <ExpandMore />}</IconButton>
                    <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>{group.name}</Typography>
                    <TypeChip type={group.type} />
                    {Number(group.receivable) > 0 && <StatusChip tone="error" label={`Owes back ${formatCurrency(group.receivable)}`} />}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography component="div" sx={{ fontSize: fs.xs, color: 'text.secondary', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      Outstanding: <CurrencyText amount={group.outstanding} bold size="sm" />
                    </Typography>
                    {canWrite && Number(group.payable) > 0 && (
                      <Button variant="contained" size="small" startIcon={<Payment />}
                        onClick={(e) => { e.stopPropagation(); setPayDialog({ direction: 'PAID', recipient: group }); }}
                        sx={{ textTransform: 'none', fontSize: fs.xs }}>
                        Pay
                      </Button>
                    )}
                  </Box>
                </Box>
                <Collapse in={Boolean(expanded[group.id])} unmountOnExit>
                  <Box sx={{ mt: 1.5 }}>
                    <DataTable columns={COLUMNS} data={g?.content || []} loading={!g || g.loading} pageSize={20}
                      emptyMessage="No commissions for this recipient." />
                    {g && g.totalElements > g.content.length && (
                      <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 0.5 }}>
                        Showing the latest {g.content.length} of {g.totalElements} — use the tabs to see all.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })
      )}

      {tab !== 0 && (
        <>
          <DataTable columns={tab === PAYMENTS_TAB ? PAYMENT_COLUMNS : COLUMNS} data={rows.content} loading={loading} pagination={false}
            emptyMessage={tab === PAYMENTS_TAB ? 'No payments yet.' : `No ${TAB_STATUS[tab].toLowerCase()} commissions.`} />
          {rows.totalElements > 0 && (
            <TablePagination component="div" count={rows.totalElements} page={page} rowsPerPage={size}
              onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[20, 50, 100]}
              sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: fs.xs } }} />
          )}
        </>
      )}

      <CommissionPaymentDialog open={Boolean(payDialog)} direction={payDialog?.direction} recipient={payDialog?.recipient || null}
        recipients={recipients} onClose={() => setPayDialog(null)}
        onDone={(p) => { setPayDialog(null); setSnackbar(`${p.paymentNumber} recorded — ${formatCurrency(p.amount)}`); refresh(); }} />

      <Dialog open={Boolean(reverse)} onClose={() => setReverse(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Reverse {reverse?.paymentNumber}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', mb: 2 }}>
            {reverse?.direction === 'PAID'
              ? 'The payment is undone in the books and its commissions become payable again.'
              : 'The entry is undone in the books and the amount is owed back again.'}
          </Typography>
          <TextField label="Reason *" fullWidth size="small" value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverse(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy || !reason.trim()} onClick={confirmReverse} sx={{ textTransform: 'none' }}>Reverse</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3500} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default Commissions;
